import { useState } from 'react'
import { useQueries } from '@tanstack/react-query'
import { Check, ChevronsUpDown, Store } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { useStoreList } from '@/hooks/useStoreList'
import { useAuthStore } from '@/lib/store/authStore'
import { cpGetLineMessagingStatus } from '@/lib/api/controlPlaneClient'
import { cn } from '@/lib/utils'
import { usePageTranslation } from '@/i18n/useTranslation'
import { lineOverviewTranslations as enT } from '@/i18n/locales/en/lineOverview'
import { lineOverviewTranslations as thT } from '@/i18n/locales/th/lineOverview'

// LINE brand mark — fallback when a store's OA has no profile picture
function LineMark({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} style={style}>
      <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63h2.386c.349 0 .63.285.63.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63.349 0 .631.285.631.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.281.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314" />
    </svg>
  )
}

// Round OA logo (bot picture) or a green LINE fallback badge
function OALogo({ src, size = 20 }: { src?: string; size?: number }) {
  const [failed, setFailed] = useState(false)
  if (src && !failed) {
    return (
      <img
        src={src}
        alt=""
        width={size}
        height={size}
        onError={() => setFailed(true)}
        className="rounded-full object-cover border border-black/10 shrink-0"
        style={{ width: size, height: size }}
      />
    )
  }
  return (
    <span
      className="rounded-full bg-[#06C755] flex items-center justify-center shrink-0"
      style={{ width: size, height: size }}
    >
      <LineMark className="text-white" style={{ width: size * 0.6, height: size * 0.6 }} />
    </span>
  )
}

/**
 * LINE OA / store switcher. Accounts with more than one store each have their
 * own LINE OA — this shows the OA's real logo and lets the merchant flip between
 * them. Renders nothing for single-store accounts.
 */
export function LineOAStoreSwitcher({ className }: { className?: string }) {
  const t = usePageTranslation({ en: enT, th: thT })
  const { data: stores = [] } = useStoreList()
  const activeKey = useAuthStore((s) => s.activeStorefrontKey)
  const setActive = useAuthStore((s) => s.setActiveStorefront)

  // Fetch each store's OA logo (bot picture). Cached hard — logos rarely change.
  const logoQueries = useQueries({
    queries: stores.map((s) => ({
      queryKey: ['line-oa-logo', s.storefront_key],
      queryFn: () => cpGetLineMessagingStatus(s.storefront_key),
      staleTime: 15 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      retry: false,
      enabled: stores.length > 1,
    })),
  })
  const logoByKey: Record<string, string | undefined> = {}
  stores.forEach((s, i) => {
    logoByKey[s.storefront_key] = logoQueries[i]?.data?.bot_picture_url || undefined
  })

  if (stores.length <= 1) return null

  const primary = stores.find((s) => s.is_primary) ?? stores[0]
  const active = stores.find((s) => s.storefront_key === activeKey) ?? primary

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex items-center gap-2 rounded-full border border-black/10 bg-white pl-1.5 pr-3 py-1',
            'text-sm font-medium text-[#353a40] shadow-sm transition-colors hover:bg-black/[0.03]',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#06C755]/40',
            className
          )}
          aria-label={t.switchOALong}
        >
          <OALogo src={logoByKey[active.storefront_key]} size={22} />
          <span className="max-w-[160px] truncate">{active.label}</span>
          <ChevronsUpDown className="w-3.5 h-3.5 text-black/40 shrink-0" />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" className="w-72">
        <DropdownMenuLabel className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Store className="w-3.5 h-3.5" /> {t.switchOA}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {stores.map((store) => {
          const isActive = store.storefront_key === active.storefront_key
          return (
            <DropdownMenuItem
              key={store.storefront_key}
              onClick={() => setActive(store.storefront_key)}
              className="flex items-center gap-2.5 py-2 cursor-pointer"
            >
              <OALogo src={logoByKey[store.storefront_key]} size={28} />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-[#353a40] truncate leading-tight">{store.label}</p>
                {store.subdomain && (
                  <p className="text-[11px] text-muted-foreground truncate leading-tight">
                    {store.subdomain}.shop.zaviago.com
                  </p>
                )}
              </div>
              {store.is_primary ? (
                <span className="text-[10px] text-muted-foreground border border-black/10 rounded px-1 py-0.5 shrink-0">
                  {t.primary}
                </span>
              ) : null}
              {isActive && <Check className="w-4 h-4 text-[#06C755] shrink-0" />}
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
